import { computed, inject, Injectable, linkedSignal } from '@angular/core';
import { isDefinedNotEmpty, isNullOrEmpty, sse, sseResource } from '@softline/core';
import { routeData } from '@softline/application';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, Observable, skip } from 'rxjs';
import { Payment } from '@softapps/wws/pos-core';
import { TerminalRepository } from '../repositories/terminal.repository';

/**
 * todos:
 *    1. Bankomat direkt nach de öffnen der Kassa ansprechen/starten (eigener POST Endpunkt in der PointOfSale API) (Lukas ruft TerminalResource.init() auf
 *
 *
 * bei Zahlung:
 *    1. bestehenden Payment add aufrufen it "terminal"
 *
 */

export interface TerminalMessage {
  message?: string;
  errorMessage?: string;
  terminalPaymentId?: number;
}

export interface TerminalEvent {
  type: TerminalEventType;
  data: TerminalMessage;
}

export const TERMINAL_EVENT_START = 'terminal/start' as const;
export const TERMINAL_EVENT_NOTIFICATION = 'terminal/notification' as const;
export const TERMINAL_EVENT_AUTHORIZATION = 'terminal/authorization' as const;
export const TERMINAL_EVENT_COMPLETE = 'terminal/complete' as const;
export const TERMINAL_EVENT_FINALIZE = 'terminal/payment/finalize' as const;

export type TerminalEventType =
  | typeof TERMINAL_EVENT_START
  | typeof TERMINAL_EVENT_NOTIFICATION
  | typeof TERMINAL_EVENT_AUTHORIZATION
  | typeof TERMINAL_EVENT_COMPLETE
  | typeof TERMINAL_EVENT_FINALIZE;

@Injectable()
export class TerminalService {
  readonly #repository = inject(TerminalRepository);

  readonly #routeData = routeData<{ configuration: { terminalSseKey?: string } }>();

  readonly #terminalKey = computed(() => {
    return this.#routeData()?.configuration?.terminalSseKey ?? undefined;
  });

  readonly #sse = sseResource({
    location: () => {
      const terminalKey = this.#terminalKey();
      return terminalKey ? { path: `/sse-service/sse/feed/${terminalKey}` } : undefined;
    },
    name: 'TerminalSSEResource',
    events: {
      /**
       * Start-Event vom Bankomat Service (Bankomat wurde informiert)
       * Loading Spinner dann ausblenden sobald das dieses Event eintrifft
       */
      'terminal/start': sse<TerminalMessage>(),

      /**
       * Allgemeine Nachrichten für Status etc.
       * Einfach untereinander anzeigen wie in einer Konsole...
       */
      'terminal/notification': sse<TerminalMessage>(),

      /**
       * Dialog mit Eingabe für code machen,
       * danach den code als query-param "aid" an folgenden Endpunkt schicken:
       * cart/{cartId}/payment/terminal/auth-request/finalize?aid={code}
       */
      'terminal/authorization': sse<TerminalMessage>(),

      /**
       * Wenn "errorMessage" befüllt (nicht leer), dann einen bestätigen button einblenden, damit user den dialog zumachen kann
       * Wenn keine errorMessage, dann 1 sekunde warten und Dialog autmatisch schließen
       */
      'terminal/complete': sse<TerminalMessage>(),
      'terminal/payment/finalize': sse<TerminalMessage>(),
    },
  });

  readonly #latestMessage$ = this.latestEvent$();

  readonly #latestStatusMessage$ = this.latestEvent$(
    (msg) => msg.type !== 'terminal/notification'
  );

  readonly #latestStatusEvent = toSignal(this.#latestStatusMessage$);
  readonly latestEvent = toSignal(this.#latestMessage$);

  readonly messages = toSignal(
    this.#sse.messages$.pipe(
      map((events: TerminalEvent[]) => {
        const displayableMessages: {
          type: 'error' | 'success' | 'info';
          message: string;
        }[] = [];

        for (const event of events) {
          if (
            !event?.data ||
            (isNullOrEmpty(event.data?.message) &&
              isNullOrEmpty(event.data?.errorMessage))
          ) {
            continue;
          }

          let type: (typeof displayableMessages)[number]['type'] = 'info';
          let message: string;

          if (isDefinedNotEmpty(event.data?.errorMessage)) {
            message = event.data?.errorMessage ?? '';
            type = 'error';
          } else {
            message = event.data?.message ?? '';
            type =
              event.type === TERMINAL_EVENT_COMPLETE &&
              !isDefinedNotEmpty(event.data?.errorMessage)
                ? 'success'
                : 'info';
          }

          displayableMessages.push({
            type,
            message,
          });
        }

        return displayableMessages;
      })
    ),
    { initialValue: [] }
  );

  readonly finalizeReceived$ = this.latestEvent$().pipe(
    skip(1),
    filter(
      (event) =>
        event.type === 'terminal/payment/finalize' ||
        (event.type === 'terminal/complete' && !isNullOrEmpty(event.data?.errorMessage))
    )
  );

  readonly state = linkedSignal(() => {
    const latestEvent = this.#latestStatusEvent();
    const eventType = latestEvent?.type;
    const hasErrorMsg =
      !!latestEvent?.data && isDefinedNotEmpty(latestEvent.data?.errorMessage);

    switch (eventType) {
      case TERMINAL_EVENT_START:
        return 'started';
      case TERMINAL_EVENT_AUTHORIZATION:
        return 'authorization';
      case TERMINAL_EVENT_COMPLETE:
        return hasErrorMsg ? 'completedFailure' : 'completed';
      case TERMINAL_EVENT_FINALIZE:
        return 'finalized';
      default:
        return 'idle';
    }
  });

  finalize(cartId: number, terminalPaymentId: number): Promise<Payment> {
    return this.#repository.finalize(cartId, terminalPaymentId);
  }

  authorize(cartId: number, aid: number | string): Promise<unknown> {
    return this.#repository.authorize(cartId, aid);
  }

  private latestEvent$(
    filterFn?: (event: TerminalEvent) => boolean
  ): Observable<TerminalEvent> {
    return this.#sse.messages$.pipe(
      map((messages) => messages.filter((msg) => (filterFn ? filterFn(msg) : true))),
      map((o) => (o.length > 0 ? o[o.length - 1] : undefined) as TerminalEvent)
    );
  }
}
